import { PlusOutlined, UserOutlined } from '@ant-design/icons';
import { Alert, Avatar, Button, Checkbox, Empty, Input, Modal, Spin, Tag, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';

import type { Passenger } from '../../../src/types.ts';
import { api, errorText } from '../lib/api.ts';
import { PassengerForm } from './PassengerForm.tsx';

interface Props {
  /** How many of each type the fare was searched for. */
  need: { adults: number; children: number; infants: number };
  value: number[];
  onChange: (ids: number[]) => void;
}

const TYPE_LABEL: Record<string, string> = { ADT: 'Adult', CHD: 'Child', INF: 'Infant' };

/** Age in whole years at today's date, from a YYYY-MM-DD birthday. */
function ageOf(birthday: string | undefined): number | null {
  if (!birthday) return null;
  const b = new Date(birthday);
  if (Number.isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age--;
  return age;
}

/** etravel decides the type from the birthday; mirror its bands when it hasn't told us. */
export function paxType(p: Passenger): string {
  if (p.passengerType) return p.passengerType;
  const age = ageOf(p.birthday);
  if (age == null) return 'ADT';
  if (age < 2) return 'INF';
  if (age < 12) return 'CHD';
  return 'ADT';
}

/** Choose saved passengers for a booking, with inline "add new". */
export function PassengerPicker({ need, value, onChange }: Props) {
  const [list, setList] = useState<Passenger[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);

  const load = () => {
    setError(null);
    api
      .passengers()
      .then((r) => setList(r.items))
      .catch((e) => setError(errorText(e)));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (!list) return [];
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((p) => `${p.firstName} ${p.lastName} ${p.passportNo}`.toLowerCase().includes(q));
  }, [list, query]);

  const chosen = (list ?? []).filter((p) => value.includes(p.id));
  const count = (t: string) => chosen.filter((p) => paxType(p) === t).length;
  const wanted: Array<[string, number]> = [
    ['ADT', need.adults],
    ['CHD', need.children],
    ['INF', need.infants],
  ];
  const complete = wanted.every(([t, n]) => count(t) === n);

  const toggle = (p: Passenger) => {
    if (value.includes(p.id)) return onChange(value.filter((id) => id !== p.id));
    const t = paxType(p);
    const cap = wanted.find(([k]) => k === t)?.[1] ?? 0;
    if (count(t) >= cap) return;
    onChange([...value, p.id]);
  };

  return (
    <div className="pax-picker">
      <div className="pax-picker__need">
        {wanted
          .filter(([, n]) => n > 0)
          .map(([t, n]) => (
            <Tag key={t} color={count(t) === n ? 'green' : 'default'} bordered={false}>
              {TYPE_LABEL[t]}s {count(t)}/{n}
            </Tag>
          ))}
        {complete && (
          <Typography.Text type="success" style={{ fontSize: 12 }}>
            All travellers chosen
          </Typography.Text>
        )}
      </div>
      <div className="pax-picker__tools">
        <Input.Search placeholder="Search name or passport" allowClear size="small" value={query} onChange={(e) => setQuery(e.target.value)} style={{ maxWidth: 260 }} />
        <Button size="small" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
          New passenger
        </Button>
      </div>
      {error && <Alert type="error" showIcon message={error} action={<Button size="small" onClick={load}>Retry</Button>} />}
      {!list && !error && <Spin />}
      {list && filtered.length === 0 && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={list.length ? 'No match.' : 'No saved passengers yet — add one.'} />}
      <div className="pax-picker__list">
        {filtered.map((p) => {
          const t = paxType(p);
          const cap = wanted.find(([k]) => k === t)?.[1] ?? 0;
          const selected = value.includes(p.id);
          const disabled = !selected && (cap === 0 || count(t) >= cap);
          return (
            <label key={p.id} className={`pax${selected ? ' pax--on' : ''}${disabled ? ' pax--off' : ''}`}>
              <Checkbox checked={selected} disabled={disabled} onChange={() => toggle(p)} />
              <Avatar size={28} icon={<UserOutlined />} />
              <span className="pax__name">
                {p.title ? `${p.title} ` : ''}
                {p.firstName} {p.lastName}
              </span>
              <span className="pax__meta">
                {p.passportNo}
                {p.passportExpiryDate ? ` · exp ${p.passportExpiryDate}` : ''}
                {p.nationalityCode ? ` · ${p.nationalityCode}` : ''}
              </span>
              <Tag bordered={false} color={t === 'ADT' ? 'blue' : t === 'CHD' ? 'purple' : 'magenta'}>
                {TYPE_LABEL[t] ?? t}
              </Tag>
            </label>
          );
        })}
      </div>
      <Modal open={adding} title="New passenger" footer={null} onCancel={() => setAdding(false)} destroyOnHidden width={720}>
        <PassengerForm
          onCancel={() => setAdding(false)}
          onSaved={(p) => {
            setAdding(false);
            // Re-fetch so we get the server's id and passengerType, then select it.
            api
              .passengers()
              .then((r) => {
                setList(r.items);
                const created = r.items.find((x) => x.id === p.id) ?? r.items.find((x) => x.passportNo === p.passportNo);
                if (created) {
                  const t = paxType(created);
                  const cap = wanted.find(([k]) => k === t)?.[1] ?? 0;
                  if (count(t) < cap) onChange([...value, created.id]);
                }
              })
              .catch(() => load());
          }}
        />
      </Modal>
    </div>
  );
}
